import { z } from 'zod';
import { EntityType } from './enums.ts';
import { Timestamp, Uuid } from './primitives.ts';

export const SyncAction = z.enum(['create', 'update', 'delete']);
export type SyncAction = z.infer<typeof SyncAction>;

const JsonObject = z.record(z.string(), z.unknown());

/** One change made on a device, queued in the outbox and applied once on the server. */
export const SyncOperation = z
  .strictObject({
    operationId: Uuid,
    householdId: Uuid,
    entityType: EntityType,
    entityId: Uuid,
    action: SyncAction,
    /** Version the device edited; required for update and delete. */
    baseVersion: z.number().int().min(1).nullable(),
    /** create: the full input; update: only changed fields; delete: null. */
    payload: JsonObject.nullable(),
    /** Values the device saw before editing, for three-way merge (update/delete). */
    base: JsonObject.nullable(),
    createdAt: Timestamp,
  })
  .superRefine((op, context) => {
    if (op.action === 'create' && op.payload === null) {
      context.addIssue({ code: 'custom', message: 'create needs a payload', path: ['payload'] });
    }
    if (op.action !== 'create' && op.baseVersion === null) {
      context.addIssue({ code: 'custom', message: 'update and delete need baseVersion', path: ['baseVersion'] });
    }
    if (op.action === 'update' && (op.payload === null || Object.keys(op.payload).length === 0)) {
      context.addIssue({ code: 'custom', message: 'update needs changed fields', path: ['payload'] });
    }
  });
export type SyncOperation = z.infer<typeof SyncOperation>;

export const PushRequest = z.strictObject({
  deviceId: Uuid,
  operations: z.array(SyncOperation).min(1).max(100),
});
export type PushRequest = z.infer<typeof PushRequest>;

/** Canonical state of one aggregate as returned by the server. */
export const EntityEnvelope = z.strictObject({
  entityType: EntityType,
  entityId: Uuid,
  householdId: Uuid,
  version: z.number().int().min(1),
  deleted: z.boolean(),
  /** Full aggregate; null for tombstones and entities the caller may not see. */
  data: JsonObject.nullable(),
});
export type EntityEnvelope = z.infer<typeof EntityEnvelope>;

export const ErrorCode = z.enum([
  'VALIDATION',
  'NOT_FOUND',
  'FORBIDDEN',
  'ID_TAKEN',
  'REFERENCE_NOT_FOUND',
  'IMMUTABLE_FIELD',
  'READ_ONLY_ENTITY',
  'SETTLEMENT_MISMATCH',
  'INTERNAL',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ConflictReason = z.enum(['FIELDS', 'FINANCIAL_GROUP', 'MERGE_INVALID', 'DELETE_STALE', 'UPDATE_DELETED']);
export type ConflictReason = z.infer<typeof ConflictReason>;

export const OperationResult = z.discriminatedUnion('status', [
  z.strictObject({ operationId: Uuid, status: z.literal('applied'), entity: EntityEnvelope }),
  z.strictObject({ operationId: Uuid, status: z.literal('duplicate'), entity: EntityEnvelope.nullable() }),
  z.strictObject({
    operationId: Uuid,
    status: z.literal('conflict'),
    conflictId: Uuid,
    reason: ConflictReason,
    fields: z.array(z.string()),
    entity: EntityEnvelope,
  }),
  z.strictObject({
    operationId: Uuid,
    status: z.literal('rejected'),
    error: z.strictObject({ code: ErrorCode, message: z.string(), details: z.unknown().optional() }),
  }),
]);
export type OperationResult = z.infer<typeof OperationResult>;

export const PushResponse = z.strictObject({
  results: z.array(OperationResult),
  serverTime: Timestamp,
});
export type PushResponse = z.infer<typeof PushResponse>;

export const Change = z.strictObject({
  seq: z.number().int().min(1),
  entityType: EntityType,
  entityId: Uuid,
  op: z.enum(['upsert', 'delete']),
  version: z.number().int().min(1),
  entity: JsonObject.nullable(),
});
export type Change = z.infer<typeof Change>;

export const ChangesResponse = z.strictObject({
  householdId: Uuid,
  changes: z.array(Change),
  nextCursor: z.number().int().min(0),
  hasMore: z.boolean(),
  serverTime: Timestamp,
});
export type ChangesResponse = z.infer<typeof ChangesResponse>;

export const HeadResponse = z.strictObject({
  householdId: Uuid,
  seq: z.number().int().min(0),
  minRetainedSeq: z.number().int().min(0),
  serverTime: Timestamp,
});
export type HeadResponse = z.infer<typeof HeadResponse>;

export const SnapshotResponse = z.strictObject({
  householdId: Uuid,
  entityType: EntityType,
  items: z.array(EntityEnvelope),
  nextAfter: Uuid.nullable(),
});
export type SnapshotResponse = z.infer<typeof SnapshotResponse>;

export const ConflictResolution = z.strictObject({
  resolution: z.enum(['SERVER', 'CLIENT', 'MERGED']),
  /** The follow-up operation that carried the chosen values (CLIENT/MERGED). */
  operationId: Uuid.nullable(),
});

/** Households the signed-in owner belongs to — the entry point after login. */
export const MembershipsResponse = z.strictObject({
  households: z.array(z.strictObject({ householdId: Uuid, role: z.enum(['OWNER']) })),
});

export const ApiError = z.strictObject({
  error: z.strictObject({
    code: z.string(),
    message: z.string(),
    correlationId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

/**
 * The fields the server owns on a new account or transaction (balance, bank provenance, currency
 * conversion), as they start. The server writes them when it stores the record; a device shows
 * them at once for a record it has not sent yet, and takes the server's values when it is sent.
 */
export function serverOwnedDefaults(type: EntityType, input: Record<string, unknown>): Record<string, unknown> {
  switch (type) {
    case 'account':
      return {
        balanceMinor: input.openingBalanceMinor ?? '0',
        availableBalanceMinor: null,
        balanceAsOf: null,
        provider: 'MANUAL',
        bankConnectionId: null,
        investmentConnectionId: null,
        lastSyncedAt: null,
        consentExpiresAt: null,
        closingBalanceMinor: null,
      };
    case 'transaction':
      return {
        externalId: null,
        provider: null,
        descriptionRaw: null,
        baseAmountMinor: null,
        fxRate: null,
        fxRateSource: null,
        fxRateDate: null,
        paymentType: null,
        isRecurringCandidate: false,
        recurringPatternId: null,
        reviewReasons: [],
        overriddenFields: [],
        importedAt: null,
      };
    default:
      return {};
  }
}
